import React from 'react';
import { Heart, ExternalLink, ArrowLeft, Globe, Users, Droplets, BookOpen, Home, Utensils, Shield, Baby, Sunrise, DivideIcon as LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

interface Charity {
  id: string;
  name: string;
  description: string;
  mission: string;
  donationUrl: string;
  icon: LucideIcon;
  color: string;
  impact: string;
}

const charities: Charity[] = [
  {
    id: 'land-of-hope',
    name: 'Land of Hope',
    description: 'Protecting children accused of witchcraft in West Africa',
    mission: 'A non-profit organization working independently of political, religious, and economic interests. We run West Africa\'s largest Children\'s Center in the Niger Delta region, providing sanctuary for children who have been accused of witchcraft and made scapegoats for difficult living conditions.',
    donationUrl: 'https://landofhope.global/en/support/',
    icon: Sunrise,
    color: 'from-emerald-500 to-teal-600',
    impact: 'Currently caring for 94 children who were accused of witchcraft, advocating for their rights and providing them with safety, education, and hope'
  },
  {
    id: 'doctors-without-borders',
    name: 'Doctors Without Borders',
    description: 'Medical humanitarian aid worldwide',
    mission: 'Providing emergency medical assistance to people affected by conflict, epidemics, disasters, or exclusion from healthcare.',
    donationUrl: 'https://www.doctorswithoutborders.org/donate',
    icon: Heart,
    color: 'from-red-500 to-red-600',
    impact: 'Reaches over 11 million people annually in 70+ countries'
  },
  {
    id: 'world-food-programme',
    name: 'World Food Programme',
    description: 'Fighting hunger worldwide',
    mission: 'The world\'s largest humanitarian organization addressing hunger and promoting food security.',
    donationUrl: 'https://www.wfp.org/donate',
    icon: Utensils,
    color: 'from-orange-500 to-orange-600',
    impact: 'Feeds 115 million people in 84 countries each year'
  },
  {
    id: 'charity-water',
    name: 'Charity: Water',
    description: 'Clean water for everyone',
    mission: 'Bringing clean and safe drinking water to people in developing countries.',
    donationUrl: 'https://www.charitywater.org/donate',
    icon: Droplets,
    color: 'from-blue-500 to-blue-600',
    impact: 'Has funded 91,000+ water projects in 29 countries'
  },
  {
    id: 'room-to-read',
    name: 'Room to Read',
    description: 'Education for children globally',
    mission: 'Creating a world where all children can pursue a quality education and reach their full potential.',
    donationUrl: 'https://www.roomtoread.org/donate',
    icon: BookOpen,
    color: 'from-green-500 to-green-600',
    impact: 'Has benefited over 23 million children across 21 countries'
  },
  {
    id: 'habitat-for-humanity',
    name: 'Habitat for Humanity',
    description: 'Building homes, communities and hope',
    mission: 'Seeking to put God\'s love into action by bringing people together to build homes, communities and hope.',
    donationUrl: 'https://www.habitat.org/donate',
    icon: Home,
    color: 'from-yellow-500 to-yellow-600',
    impact: 'Has helped more than 46 million people achieve strength, stability and independence'
  },
  {
    id: 'oxfam',
    name: 'Oxfam',
    description: 'Fighting inequality to end poverty',
    mission: 'Working with others to find lasting solutions to poverty and injustice.',
    donationUrl: 'https://www.oxfam.org/en/donate',
    icon: Globe,
    color: 'from-teal-500 to-teal-600',
    impact: 'Reaches 21 million people directly and 61 million indirectly'
  },
  {
    id: 'save-the-children',
    name: 'Save the Children',
    description: 'Giving children a healthy start in life',
    mission: 'Inspiring breakthroughs in the way the world treats children and achieving immediate and lasting change.',
    donationUrl: 'https://www.savethechildren.org/us/ways-to-help/ways-to-give/donate',
    icon: Baby,
    color: 'from-pink-500 to-pink-600',
    impact: 'Reached 144 million children in 2022 across 100+ countries'
  },
  {
    id: 'amnesty-international',
    name: 'Amnesty International',
    description: 'Defending human rights worldwide',
    mission: 'Working to free all prisoners of conscience and prevent torture and abolish the death penalty.',
    donationUrl: 'https://www.amnesty.org/en/donate/',
    icon: Shield,
    color: 'from-purple-500 to-purple-600',
    impact: 'Has helped free more than 50,000 prisoners of conscience'
  }
];

export const Charities: React.FC = () => {
  const { t } = useTranslation();
  
  const handleDonate = (url: string, charityName: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-purple-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-purple-600 hover:text-purple-700 font-medium mb-6 transition-colors duration-200"
          >
            <ArrowLeft size={20} />
            <span>{t('dana.backToHome')}</span>
          </Link>
          
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-3xl font-bold">ॐ</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('dana.practiceDana')}</h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('dana.chooseCharityDescription')}
            </p>
          </div>
        </div>

        {/* Charities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {charities.map((charity) => {
            const Icon = charity.icon;
            return (
              <div
                key={charity.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-100 overflow-hidden flex flex-col h-full"
              >
                {/* Card Header */}
                <div className={`bg-gradient-to-r ${charity.color} p-6 text-white`}>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <Icon size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{charity.name}</h3>
                      <p className="text-white/90 text-sm">{charity.description}</p>
                    </div>
                  </div>
                </div>

                {/* Card Content - Flex grow to fill available space */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{t('dana.mission')}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{charity.mission}</p>
                  </div>

                  <div className="mb-6 flex-grow">
                    <h4 className="font-semibold text-gray-900 mb-2">{t('dana.impact')}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{charity.impact}</p>
                  </div>

                  {/* Donation Button - Always at bottom */}
                  <button
                    onClick={() => handleDonate(charity.donationUrl, charity.name)}
                    className={`w-full bg-gradient-to-r ${charity.color} hover:opacity-90 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2 mt-auto`}
                  >
                    <Heart size={16} />
                    <span>{t('dana.donate')}</span>
                    <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Message */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-8 border border-amber-200 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('dana.spiritOfDana')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('dana.spiritDescription')}
            </p>
            <p className="text-amber-700 italic font-medium">
              {t('dana.anneFrankQuote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};